import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import KakaoAdBanner from './KakaoAdBanner';

function ForwardCalculator() {

  const [formData, setFormData] = useState({
    current_age: 30,
    retirement_age: 60,
    monthly_payment: 1000000,
    annual_return_rate: 7.0,
    pension_yield_rate: 4.0
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const tableRef = useRef(null);

  // 화면 크기 변화 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // API URL 직접 지정 (프로덕션 환경)
    const apiUrl = 'https://pension-backend-100694510241.asia-northeast3.run.app/api/calculate-pension';
    
    try {
      const response = await axios.post(apiUrl, formData);
      
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || '계산 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error('API 호출 실패:', err);
      console.error('요청 URL:', apiUrl);
      console.error('응답 상태:', err.response?.status);
      console.error('응답 데이터:', err.response?.data);
      
      if (err.response?.status === 404) {
        setError('API 엔드포인트를 찾을 수 없습니다. Firebase 설정을 확인해주세요.');
      } else if (err.response?.status >= 500) {
        setError('서버 내부 오류가 발생했습니다. Cloud Run 서비스 상태를 확인해주세요.');
      } else if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
        setError('네트워크 연결 오류: Cloud Run 서비스에 연결할 수 없습니다.');
      } else {
        setError(`연결 실패: ${err.message}. 개발자 도구 콘솔을 확인해주세요.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Chart.js 그래프 생성 (안전한 초기화)
  useEffect(() => {
    if (!result || !chartRef.current) {
      return;
    }

    // Chart.js 라이브러리 로딩 대기
    const initChart = () => {
      try {
        if (!window.Chart) {
          console.warn('Chart.js가 아직 로드되지 않음');
          return;
        }

        // 기존 차트 안전하게 파괴
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }

        const ctx = chartRef.current?.getContext('2d');
        if (!ctx) {
          console.warn('Canvas 컨텍스트를 가져올 수 없음');
          return;
        }
        
        const labels = result.cash_flow_details.map(detail => `${detail.age}세`);
        const accumulatedData = result.cash_flow_details.map(detail => detail.accumulated_amount);
        const paidData = result.cash_flow_details.map(detail => detail.paid_amount);
        const interestData = result.cash_flow_details.map(detail => detail.interest_earned);

        chartInstanceRef.current = new window.Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [
              {
                label: '적립액',
                data: accumulatedData,
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.1,
                fill: true
              },
              {
                label: '납입액',
                data: paidData,
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                tension: 0.1
              },
              {
                label: '수익',
                data: interestData,
                borderColor: 'rgb(255, 205, 86)',
                backgroundColor: 'rgba(255, 205, 86, 0.2)',
                tension: 0.1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            aspectRatio: isMobile ? 1 : 2,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              title: {
                display: true,
                text: '연금 적립 과정 시뮬레이션',
                font: {
                  size: isMobile ? 12 : 16
                }
              },
              legend: {
                display: true,
                position: isMobile ? 'bottom' : 'top',
                labels: {
                  font: {
                    size: isMobile ? 10 : 12
                  },
                  boxWidth: isMobile ? 12 : 15
                }
              }
            },
            scales: {
              x: {
                ticks: {
                  font: {
                    size: isMobile ? 8 : 12
                  },
                  maxRotation: isMobile ? 45 : 0
                }
              },
              y: {
                beginAtZero: true,
                ticks: {
                  font: {
                    size: isMobile ? 8 : 12
                  },
                  callback: function(value, index, values) {
                    const formatter = new Intl.NumberFormat('ko-KR', {
                      notation: isMobile ? 'compact' : 'standard',
                      compactDisplay: 'short'
                    });
                    return formatter.format(value) + '원';
                  }
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('차트 생성 중 오류:', error);
      }
    };

    // Chart.js 로딩 대기 후 초기화
    const timer = setTimeout(initChart, 200);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      try {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
      } catch (error) {
        console.warn('차트 정리 중 오류:', error);
      }
    };
  }, [result, isMobile]);

  // DataTable 초기화 (안전한 버전)
  useEffect(() => {
    if (!result || !tableRef.current) {
      return;
    }

    const initDataTable = () => {
      try {
        // jQuery와 DataTables 라이브러리 확인
        if (!window.$ || !window.$.fn || !window.$.fn.DataTable) {
          console.warn('jQuery 또는 DataTables가 로드되지 않음');
          return;
        }

        const table = tableRef.current;
        if (!table) {
          console.warn('테이블 참조가 유효하지 않음');
          return;
        }

        // 기존 DataTable 안전하게 파괴
        if (window.$.fn.DataTable.isDataTable(table)) {
          window.$(table).DataTable().destroy();
        }
        
        // 새로운 DataTable 생성
        window.$(table).DataTable({
          language: {
            url: 'https://cdn.datatables.net/plug-ins/1.13.6/i18n/Korean.json'
          },
          pageLength: 10,
          responsive: true,
          dom: 'Bfrtip',
          scrollX: true,
          autoWidth: false
        });
      } catch (error) {
        console.warn('DataTable 초기화 실패:', error);
      }
    };

    // 라이브러리 로딩 대기 후 초기화
    const timer = setTimeout(initDataTable, 300);

    return () => {
      clearTimeout(timer);
      try {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const table = tableRef.current;
        if (table && window.$ && window.$.fn && window.$.fn.DataTable) {
          if (window.$.fn.DataTable.isDataTable(table)) {
            window.$(table).DataTable().destroy();
          }
        }
      } catch (error) {
        console.warn('DataTable 정리 실패:', error);
      }
    };
  }, [result]);

  return (
    <div className="bg-light min-vh-100">
      {/* 헤더 */}
      <header className="bg-gradient-primary text-white py-4">
        <div className="container">
          <h1 className="h2 mb-2">
            <i className="fas fa-calculator me-2"></i>
            라떼비버 노후연금 계산기 - 일반 계산
          </h1>
          <p className="lead mb-0">
            월 저축액으로 노후 현금흐름을 계산합니다
          </p>
        </div>
      </header>

      {/* 카카오 애드핏 배너 */}
      <div className="container">
        <KakaoAdBanner className="my-4" />
      </div>

      <main className="container-fluid py-4">
        <div className="row">
          {/* 입력 폼 */}
          <div className="col-xl-4 col-lg-5 mb-4">
            <div className="card shadow">
              <div className="card-header py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  <i className="fas fa-edit me-2"></i>
                  계산 조건 입력
                </h6>
              </div>
              <div className="card-body">
                <form onSubmit={handleCalculate}>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">현재 나이</label>
                      <input
                        type="number"
                        name="current_age"
                        value={formData.current_age}
                        onChange={handleInputChange}
                        className="form-control"
                        min="20"
                        max="65"
                        required
                      />
                      <small className="text-muted">만 나이</small>
                    </div>
                    <div className="col-6">
                      <label className="form-label">은퇴 나이</label>
                      <input
                        type="number"
                        name="retirement_age"
                        value={formData.retirement_age}
                        onChange={handleInputChange}
                        className="form-control"
                        min="50"
                        max="80"
                        required
                      />
                      <small className="text-muted">은퇴 예정 나이</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">월 납입액 (원)</label>
                    <input
                      type="number"
                      name="monthly_payment"
                      value={formData.monthly_payment}
                      onChange={handleInputChange}
                      className="form-control"
                      min="10000"
                      step="10000"
                      required
                    />
                    <small className="text-muted">매월 정기 납입할 금액</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">연 복리 수익률 (%)</label>
                    <input
                      type="number"
                      name="annual_return_rate"
                      value={formData.annual_return_rate}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      max="20"
                      step="0.1"
                      required
                    />
                    <small className="text-muted">납입 기간 동안 예상 수익률</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">은퇴 후 포트폴리오 연 배당률 (%)</label>
                    <input
                      type="number"
                      name="pension_yield_rate"
                      value={formData.pension_yield_rate}
                      onChange={handleInputChange}
                      className="form-control"
                      min="0"
                      max="10"
                      step="0.1"
                      required
                    />
                    <small className="text-muted">배당주, 월세 등 현금흐름 수익률</small>
                  </div>

                  {error && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary w-100"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        계산 중...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-calculator me-2"></i>
                        연금 계산하기
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* 결과 표시 */}
          <div className="col-xl-8 col-lg-7">
            {!result && (
              <div className="card shadow">
                <div className="card-body text-center py-5">
                  <i className="fas fa-chart-line fa-3x text-gray-300 mb-3"></i>
                  <h5 className="text-gray-600">계산 결과가 여기에 표시됩니다</h5>
                  <p className="text-gray-500">좌측 폼에서 조건을 입력하고 계산해보세요!</p>
                </div>
              </div>
            )}

            {result && (
              <>
                {/* 결과 요약 카드들 */}
                <div className="row mb-4">
                  <div className="col-xl-4 col-md-6 mb-4">
                    <div className="card border-left-primary shadow h-100 py-2">
                      <div className="card-body">
                        <div className="row no-gutters align-items-center">
                          <div className="col mr-2">
                            <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                              최종 적립액
                            </div>
                            <div className="h5 mb-0 font-weight-bold text-gray-800">
                              {formatNumber(result.final_accumulated_amount)}원
                            </div>
                          </div>
                          <div className="col-auto">
                            <i className="fas fa-piggy-bank fa-2x text-gray-300"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-4 col-md-6 mb-4">
                    <div className="card border-left-success shadow h-100 py-2">
                      <div className="card-body">
                        <div className="row no-gutters align-items-center">
                          <div className="col mr-2">
                            <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                              월 현금흐름
                            </div>
                            <div className="h5 mb-0 font-weight-bold text-gray-800">
                              {formatNumber(result.monthly_cash_flow)}원
                            </div>
                          </div>
                          <div className="col-auto">
                            <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-4 col-md-6 mb-4">
                    <div className="card border-left-info shadow h-100 py-2">
                      <div className="card-body">
                        <div className="row no-gutters align-items-center">
                          <div className="col mr-2">
                            <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                              수익률
                            </div>
                            <div className="h5 mb-0 font-weight-bold text-gray-800">
                              {((result.final_accumulated_amount / result.total_paid_amount - 1) * 100).toFixed(1)}%
                            </div>
                          </div>
                          <div className="col-auto">
                            <i className="fas fa-percentage fa-2x text-gray-300"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 그래프 */}
                <div className="card shadow mb-4">
                  <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-primary">
                      <i className="fas fa-chart-area me-2"></i>
                      적립 과정 시각화
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="chart-area" style={{ 
                      position: 'relative', 
                      height: isMobile ? '300px' : '400px', 
                      width: '100%',
                      overflow: 'hidden'
                    }}>
                      <canvas ref={chartRef}></canvas>
                    </div>
                  </div>
                </div>


                {/* 상세 테이블 */}
                <div className="card shadow mb-4">
                  <div className="card-header py-3">
                    <h6 className="m-0 font-weight-bold text-primary">
                      <i className="fas fa-table me-2"></i>
                      적립 과정 상세 데이터
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-bordered" ref={tableRef} width="100%" cellSpacing="0">
                        <thead>
                          <tr>
                            <th>연차</th>
                            <th>나이</th>
                            <th>적립액</th>
                            <th>납입액</th>
                            <th>수익</th>
                            <th>수익률</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.cash_flow_details.map((detail, index) => (
                            <tr key={index}>
                              <td>{detail.year}년</td>
                              <td>{detail.age}세</td>
                              <td className="text-success font-weight-bold">
                                {formatNumber(detail.accumulated_amount)}원
                              </td>
                              <td>{formatNumber(detail.paid_amount)}원</td>
                              <td className="text-warning font-weight-bold">
                                +{formatNumber(detail.interest_earned)}원
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {((detail.interest_earned / detail.paid_amount) * 100).toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ForwardCalculator;